import React, { useEffect, useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonTitle,
  IonToolbar,
  IonContent,
  IonItem,
  IonLabel,
  IonAccordionGroup,
  IonAccordion,
  IonButton,
  IonAlert,
  IonButtons,
  IonBackButton,
  IonThumbnail,
  IonImg,
  IonIcon,
} from '@ionic/react';
import { play } from 'ionicons/icons';
import { doc, getDoc, Timestamp } from 'firebase/firestore';
import { firestore } from '../../../firebase';
import { useParams, useHistory } from 'react-router';
import { Routine, MachineSession } from '../../../datamodels';

import { useAuth } from '../../../auth';
import {
  startSession,
  addMachineSession,
} from '../../../services/TrainingSessionService';
import { useTrainingStore } from '../../../stores/useTrainingStore';
import { createLocalId, routineRepository } from '../../../repositories/LocalRepositories';

import './RoutineDetails.css';
import { getMachineImageUrl } from '../../../utils/MachineImageUtils';

const FALLBACK_IMG = '/assets/pl_icon/icon_f_matt.png';

const RoutineDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [routine, setRoutine] = useState<Routine | null>(null);
  const [machineNames, setMachineNames] = useState<{ [id: string]: string }>(
    {}
  );
  const [machineImages, setMachineImages] = useState<{ [id: string]: string }>(
    {}
  );
  const [showConfirmAlert, setShowConfirmAlert] = useState(false);

  const { userId } = useAuth();
  const history = useHistory();
  const setMachineSessionsInStore = useTrainingStore(s => s.setMachineSessions);
  const startLocalSession = useTrainingStore(s => s.startLocalSession);

  useEffect(() => {
    const fetchRoutine = async () => {
      try {
        if (!id) return;
        const found = await routineRepository.get(id);
        if (found) {
          setRoutine(found);
        } else {
          console.log('No routine found with id:', id);
        }
      } catch (error) {
        console.error('Error fetching routine:', error);
      }
    };

    fetchRoutine();
  }, [id]);

  useEffect(() => {
    const fetchMachineNames = async () => {
      if (!routine || !routine.machineSessions) return;

      const machineIds = routine.machineSessions.map(ms => ms.machine_ref);
      const newNames: { [key: string]: string } = {};
      const newImages: { [key: string]: string } = {};

      for (const mId of machineIds) {
        if (!mId) continue;
        try {
          const mRef = doc(firestore, 'machines', mId);
          const mSnap = await getDoc(mRef);
          if (mSnap.exists()) {
            const machine = mSnap.data();
            newNames[mId] = machine.title;
            const imageUrl = getMachineImageUrl(machine);
            if (imageUrl) newImages[mId] = imageUrl;
          } else {
            newNames[mId] = '(Unknown Machine)';
          }
        } catch (err) {
          console.error('Error fetching machine doc:', err);
          newNames[mId] = '(Error)';
        }
      }

      setMachineNames(newNames);
      setMachineImages(newImages);
    };

    fetchMachineNames();
  }, [routine]);

  const handleStartWorkout = async () => {
    if (!routine || !userId) return;

    let newSessionId: string | null = null;

    // 1) Create a fresh "training_sessions" document in Firestore.
    await startSession(
      userId,
      generatedId => (newSessionId = generatedId),
      () => {},
      () => {}
    );
    if (!newSessionId) return;

    // 2) Build the same machine-session state locally that is written to Firestore.
    // addMachineSession persists data, but intentionally does not update UI state itself.
    let newMachineSessions: MachineSession[] = [];
    const noOpSetter: React.Dispatch<React.SetStateAction<MachineSession[]>> =
      () => {};

    for (const ms of routine.machineSessions) {
      const machineRefId = ms.machine_ref;
      const machinePlaceholder = { id: machineRefId } as any;
      const exerciseName = ms.exercise_name ?? '';
      const machineSessionId = createLocalId();

      for (const set of ms.sets) {
        await addMachineSession(
          userId,
          newSessionId,
          machinePlaceholder,
          exerciseName,
          set.reps,
          set.weight,
          newMachineSessions,
          noOpSetter,
          machineSessionId
        );

        const existingIndex = newMachineSessions.findIndex(
          session => session.id === machineSessionId
        );

        if (existingIndex === -1) {
          newMachineSessions = [
            ...newMachineSessions,
            {
              id: machineSessionId,
              training_session_id: newSessionId,
              machine_ref: doc(firestore, 'machines', machineRefId),
              exercise_name: exerciseName,
              date_used: Timestamp.now(),
              sets: [{ ...set }],
              user_id: userId,
            },
          ];
        } else {
          newMachineSessions = newMachineSessions.map((session, index) =>
            index === existingIndex
              ? { ...session, sets: [...session.sets, { ...set }] }
              : session
          );
        }
      }
    }

    // 3) Start the local session in one place so TrainingStart can render it immediately.
    startLocalSession(newSessionId, Date.now());
    setMachineSessionsInStore(newMachineSessions);

    // 7) Finally navigate to the “Start Training” page:
    history.push('/my/trainingstart');
  };

  if (!routine) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/my/routines" text="" />
            </IonButtons>
            <IonTitle>Routine Details</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            Loading routine...
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/my/routines" text="" />
          </IonButtons>
          <IonTitle>{routine.name}</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="routine-details-content">
        <section className="routine-details-hero">
          <div>
            <span>Saved workout</span>
            <h1>{routine.name}</h1>
            <p>
              {routine.machineSessions.length}{' '}
              {routine.machineSessions.length === 1 ? 'exercise' : 'exercises'}{' '}
              ready to go.
            </p>
          </div>
          <IonButton onClick={() => setShowConfirmAlert(true)}>
            <IonIcon icon={play} slot="start" />
            Start workout
          </IonButton>
        </section>

        <IonAlert
          isOpen={showConfirmAlert}
          onDidDismiss={() => setShowConfirmAlert(false)}
          header="Start Workout"
          message="Do you want to start a workout based on the routine?"
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
              handler: () => setShowConfirmAlert(false),
            },
            {
              text: 'Start',
              handler: () => {
                setShowConfirmAlert(false);
                handleStartWorkout();
              },
            },
          ]}
        />

        <div className="routine-details-section-heading">
          <span>Workout plan</span>
          <h2>Machines &amp; sets</h2>
        </div>

        <div className="routine-accordion-card">
        <IonAccordionGroup>
          {routine.machineSessions.map((ms, idx) => {
            const machineTitle = machineNames[ms.machine_ref] || '(Loading...)';
            const imageSrc = machineImages[ms.machine_ref] || FALLBACK_IMG;

            const exerciseToShow =
              ms.exercise_name &&
              ms.exercise_name.trim() !== '' &&
              ms.exercise_name.trim() !== machineTitle.trim()
                ? ms.exercise_name
                : null;

            return (
              <IonAccordion key={idx} value={`routine-${idx}`}>
                <IonItem slot="header" color="light" className="routine-machine-header">
                  <IonThumbnail slot="start" className="routine-machine-thumb">
                    <IonImg src={imageSrc} alt={machineTitle} />
                  </IonThumbnail>
                  <IonLabel className="routine-machine-label">
                    <div className="routine-machine-title">{machineTitle}</div>
                    <div className="routine-machine-subtitle">
                      {exerciseToShow || `${ms.sets.length} ${ms.sets.length === 1 ? 'set' : 'sets'}`}
                    </div>
                  </IonLabel>
                </IonItem>

                <div className="routine-sets" slot="content">
                  <div className="routine-set-row routine-set-row--header">
                    <span>Set</span>
                    <span>Reps</span>
                    <span>Weight</span>
                  </div>
                  {ms.sets.map((set, i) => (
                    <div className="routine-set-row" key={i}>
                      <span>{i + 1}</span>
                      <strong>{set.reps}</strong>
                      <strong>{set.weight} kg</strong>
                    </div>
                  ))}
                </div>
              </IonAccordion>
            );
          })}
        </IonAccordionGroup>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default RoutineDetails;
